import { faShare } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ContactSearchComponent from 'components/app/support-desk/tickets-preview/ContactSearchComponent';
import MessageTemplateAutocomplete from 'components/common/MessageTemplateAutocomplete';
import MessageTemplateSelector from 'components/common/MessageTemplateSelector';
import Editor from 'components/editor/Editor';
import Loading from 'components/loading/Loading';
import FailedRequest from 'components/requests-response/FailedRequest';
import SuccessRequest from 'components/requests-response/SuccessRequest';
import useApi from 'helpers/api';
import { processHtmlForEmail } from 'helpers/utils';
import { useEffect, useRef, useState } from 'react';
import { Button, Card, Form } from 'react-bootstrap';

import AgentsService from 'services/Agents.service';

const ForwardNoteComponent = ({
  show,
  handleClose,
  ticketId,
  customerId,
  ticketSubject,
  ticketDescription,
  refreshNotes,
  refreshLogs
}) => {
  if (!show) return null; // Não renderiza se show=false

  const api = useApi();
  const form = useRef(null);
  const editorRef = useRef(null);
  const [validated, setValidated] = useState(false);
  const [body, setBody] = useState({
    note: '',
    typeView: 'PUBLIC',
    recipients: '',
    subject: ''
  });
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [error, setError] = useState({});
  const [loading, setLoading] = useState(false);
  const [editorContent, setEditorContent] = useState('');
  const [recipients, setRecipients] = useState('');

  // Reset form when opened
  useEffect(() => {
    const fetchSignature = async () => {
      try {
        const response = await AgentsService.getSignature();
        if (response.signature) {
          const signature = `<br><br>${response.signature}`;
          handleSetBody('note', signature);
          setEditorContent(signature);
        }
      } catch (error) {
        console.error('Error fetching signature:', error);
      }
    };

    if (show) {
      handleSetBody('recipients', '');
      handleSetBody('subject', `Fwd: ${ticketSubject || ''}`);
      // Initialize with empty content, it will be populated by the next useEffect
      handleSetBody('note', '');
      setValidated(false);
      fetchSignature();
    }
  }, [show, ticketSubject]);

  // Set initial note content when ticket description changes
  useEffect(() => {
    if (ticketDescription) {
      // Append description to existing note (which might contain signature)
      setBody(prev => ({
        ...prev,
        note: `${prev.note}${ticketDescription}`
      }));
      setEditorContent(prev => `${prev}${ticketDescription}`);
    }
  }, [ticketDescription]);

  useEffect(() => {
    if (recipients) {
      handleSetBody('recipients', recipients);
    }
  }, [recipients]);

  const onSubmit = async e => {
    e.preventDefault();
    e.stopPropagation();
    setValidated(true);

    if (
      !form.current.checkValidity() ||
      !body.recipients ||
      !body.subject ||
      !body.note ||
      body.note === '<p><br></p>'
    ) {
      return;
    }

    setLoading(true);

    const processedNote = processHtmlForEmail(body.note);
    const processedBody = { ...body, note: processedNote };

    try {
      const response = await api.ticketNotes.forward(ticketId, processedBody);

      if (response && response.id) {
        setShowSuccess(true);
        refreshLogs();
        refreshNotes();
        clearFields();
        handleClose();
      } else {
        setError(response || { description: 'Erro ao encaminhar nota' });
        setShowError(true);
      }
    } catch (err) {
      setError({
        description:
          'Erro ao encaminhar nota: ' + (err.message || 'Erro desconhecido')
      });
      setShowError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSetBody = (fieldName, fieldValue) => {
    setBody(prevBody => ({ ...prevBody, [fieldName]: fieldValue }));
  };

  const handleEditorChange = value => {
    handleSetBody('note', value);
    setEditorContent(value);
  };

  const clearFields = () => {
    setBody({
      note: '',
      typeView: 'PUBLIC',
      recipients: '',
      subject: ''
    });
    setEditorContent('');
  };

  return (
    <>
      {loading && <Loading />}

      <SuccessRequest
        message="Nota encaminhada com sucesso"
        showAlert={showSuccess}
        setShowAlert={() => setShowSuccess(false)}
      />

      <FailedRequest
        message={error.description}
        showAlert={showError}
        setShowAlert={() => setShowError(false)}
      />

      <Card>
        <Card.Body className="pb-0">
          <Form ref={form} validated={validated} onSubmit={onSubmit} noValidate>
            <div className="mb-2">
              <ContactSearchComponent
                label="Para"
                required
                customerId={customerId}
                onChange={setRecipients}
              />
              <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
              <Form.Control.Feedback type="invalid">
                Este campo é obrigatório.
              </Form.Control.Feedback>
            </div>

            <div className="mb-2">
              <Form.Label className="mb-1 fs-10">
                Assunto <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                required
                value={body.subject}
                onChange={e => handleSetBody('subject', e.target.value)}
              />
              <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
              <Form.Control.Feedback type="invalid">
                Este campo é obrigatório.
              </Form.Control.Feedback>
            </div>

            <div className="mb-2">
              <Form.Label className="mb-1 fs-10 d-flex align-items-center justify-content-between">
                <span>
                  Mensagem <span className="text-danger">*</span>
                </span>
                <MessageTemplateSelector
                  type="EMAIL"
                  ticketId={ticketId}
                  editorRef={editorRef}
                  onTemplateApplied={content => {
                    handleSetBody('note', content);
                  }}
                />
              </Form.Label>
              <Editor
                value={handleEditorChange}
                height="300px"
                data={editorContent}
                ref={editorRef}
              />
              <MessageTemplateAutocomplete
                editorRef={editorRef}
                type="EMAIL"
                ticketId={ticketId}
                onTemplateApplied={content => {
                  handleSetBody('note', content);
                }}
              />
              {validated && (!body.note || body.note === '<p><br></p>') && (
                <div className="text-danger small mt-1">
                  Este campo é obrigatório.
                </div>
              )}
            </div>
          </Form>
        </Card.Body>

        <Card.Footer className="d-flex justify-content-end">
          <Button
            variant="falcon-default"
            type="button"
            className="me-2"
            onClick={handleClose}
          >
            Fechar
          </Button>
          <Button
            variant="primary"
            type="button"
            onClick={onSubmit}
            disabled={loading}
          >
            <FontAwesomeIcon icon={faShare} className="me-1" />
            {loading ? 'Encaminhando...' : 'Encaminhar'}
          </Button>
        </Card.Footer>
      </Card>
    </>
  );
};

export default ForwardNoteComponent;
